import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import { FaCreditCard, FaCheck, FaClock, FaExclamationTriangle, FaEye, FaSync, FaFilter, FaSearch, FaTimes, FaSortAmountDown, FaSortAmountUp, FaCalendarAlt } from 'react-icons/fa';
import BlurredComponent from './MockPages/SEODashboard/BlurredComponent';

// Blue-gray theme colors
const THEME = {
  primary: '#6366f1',
  primaryLight: '#818cf8',
  primaryDark: '#4f46e5',
  bgPrimary: '#0f1419',
  bgSecondary: '#1a1f2e',
  bgCard: 'rgba(30, 35, 50, 0.6)',
  border: 'rgba(100, 150, 200, 0.15)',
  borderAccent: 'rgba(99, 102, 241, 0.3)',
  light: '#e8eaed',
  muted: 'rgba(255, 255, 255, 0.5)',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
};

// Styled Components
const Container = styled.div`
  padding: 24px;
  background: linear-gradient(180deg, ${THEME.bgPrimary} 0%, ${THEME.bgSecondary} 100%);
  color: #fff;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${THEME.border};
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${THEME.light};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    color: ${THEME.primary};
  }
`;

const SearchAndFilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${THEME.bgCard};
  border: 1px solid ${THEME.border};
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:focus-within {
    border-color: ${THEME.borderAccent};
    background: rgba(99, 102, 241, 0.1);
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.2);
  }

  svg {
    color: ${THEME.muted};
    font-size: 18px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: ${THEME.light};
  font-size: 14px;
  font-weight: 500;

  &::placeholder {
    color: ${THEME.muted};
  }
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: ${THEME.muted};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: ${THEME.light};
    background: rgba(99, 102, 241, 0.15);
  }

  svg {
    font-size: 16px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  background: ${THEME.bgCard};
  border: 1px solid ${THEME.border};
  border-radius: 8px;
  padding: 10px 12px;
  color: ${THEME.light};
  font-size: 13px;
  outline: none;
  transition: all 0.3s ease;
  min-width: 140px;

  &:focus {
    border-color: ${THEME.borderAccent};
    background: rgba(99, 102, 241, 0.1);
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.2);
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;
  }
`;

const SortSelect = styled.select`
  background: ${THEME.bgCard};
  border: 1px solid ${THEME.border};
  border-radius: 8px;
  padding: 10px 12px;
  color: ${THEME.light};
  font-size: 13px;
  outline: none;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    border-color: ${THEME.borderAccent};
    background: rgba(99, 102, 241, 0.1);
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.2);
  }

  option {
    background: ${THEME.bgSecondary};
    color: ${THEME.light};
  }
`;

const SortOrderButton = styled.button`
  background: ${THEME.bgCard};
  border: 1px solid ${THEME.border};
  color: ${THEME.light};
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${THEME.borderAccent};
    background: rgba(99, 102, 241, 0.15);
    color: #fff;
  }
`;

const FilterButton = styled.button`
  background: ${props => props.active ? `linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.primaryDark} 100%)` : THEME.bgCard};
  border: 1px solid ${props => props.active ? THEME.primary : THEME.border};
  color: ${props => props.active ? '#fff' : THEME.light};
  padding: 10px 18px;
  border-radius: 24px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'};

  &:hover {
    border-color: ${THEME.primary};
    background: ${props => props.active ? `linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.primaryDark} 100%)` : 'rgba(99, 102, 241, 0.15)'};
    transform: translateY(-1px);
  }
`;

const RefreshButton = styled.button`
  background: ${THEME.bgCard};
  border: 1px solid ${THEME.border};
  color: ${THEME.light};
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${THEME.borderAccent};
    color: #fff;
    background: rgba(99, 102, 241, 0.15);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    ${props => props.spinning ? 'animation: spin 1s linear infinite;' : ''}
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const TableWrapper = styled.div`
  background: ${THEME.bgCard};
  border-radius: 16px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid ${THEME.border};
  min-height: 0;
  max-height: 100%;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 1.2fr 1fr 1fr 120px 100px 140px 140px 90px;
  background: rgba(99, 102, 241, 0.15);
  padding: 18px 20px;
  font-weight: 600;
  font-size: 12px;
  color: ${THEME.primaryLight};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 2px solid ${THEME.borderAccent};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableBody = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  
  /* Visible scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 150, 200, 0.3) rgba(0, 0, 0, 0.2);
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(100, 150, 200, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(100, 150, 200, 0.5);
  }
`;

const PaymentRow = styled.div`
  display: grid;
  grid-template-columns: 50px 1.2fr 1fr 1fr 120px 100px 140px 140px 90px;
  padding: 18px 20px;
  border-bottom: 1px solid ${THEME.border};
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-left: 3px solid ${THEME.primary};
    padding-left: 17px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-self: start;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  ${props => {
    switch (props.status) {
      case 'pending':
        return `background: linear-gradient(135deg, ${THEME.warning} 0%, #d97706 100%); color: #fff;`;
      case 'paid':
        return `background: linear-gradient(135deg, ${THEME.success} 0%, #059669 100%); color: #fff;`;
      case 'expired':
        return 'background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); color: #fff;';
      case 'cancelled':
        return `background: linear-gradient(135deg, ${THEME.danger} 0%, #dc2626 100%); color: #fff;`;
      default:
        return 'background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); color: #fff;';
    }
  }}
`;

const Amount = styled.span`
  font-weight: 700;
  color: ${THEME.success};
  font-size: 16px;
  text-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
`;

const ClientInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ClientName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${THEME.light};
`;

const ClientEmail = styled.div`
  font-size: 12px;
  color: ${THEME.muted};
`;

const Description = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin-top: 4px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DateText = styled.div`
  font-size: 12px;
  color: ${THEME.muted};
`;

const Auth0Id = styled.div`
  font-size: 11px;
  color: ${THEME.muted};
  font-family: 'Courier New', monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
`;

const RowNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${THEME.muted};
  text-align: center;
`;

const NameWithDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.primaryDark} 100%);
  border: none;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: ${THEME.muted};
  
  svg {
    font-size: 64px;
    margin-bottom: 20px;
    opacity: 0.3;
    color: ${THEME.primary};
  }
  
  h3 {
    font-size: 18px;
    margin-bottom: 8px;
    color: ${THEME.light};
  }
  
  p {
    font-size: 14px;
    color: ${THEME.muted};
  }
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, ${THEME.danger} 0%, #dc2626 100%);
  color: #fff;
  padding: 14px 18px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: ${THEME.light};
  font-size: 14px;
  gap: 10px;
  
  svg {
    animation: spin 1s linear infinite;
    color: ${THEME.primary};
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Mock data for payments
const mockPayments = [
  {
    id: 'pm_1',
    clientName: 'John Smith',
    clientEmail: 'john.smith@example.com',
    amount: 150.00,
    currency: 'USD',
    description: 'Technical consultation on React optimization',
    status: 'pending',
    createdAt: '2024-01-15T10:30:00Z',
    paidAt: null,
    rapydCheckoutId: 'checkout_123'
  },
  {
    id: 'pm_2',
    clientName: 'Sarah Johnson',
    clientEmail: 'sarah.johnson@example.com',
    amount: 200.00,
    currency: 'USD',
    description: 'Database design consultation',
    status: 'paid',
    createdAt: '2024-01-14T14:20:00Z',
    paidAt: '2024-01-14T15:45:00Z',
    rapydCheckoutId: 'checkout_124'
  },
  {
    id: 'pm_3',
    clientName: 'Mike Wilson',
    clientEmail: 'mike.wilson@example.com',
    amount: 75.00,
    currency: 'USD',
    description: 'Quick debugging session',
    status: 'expired',
    createdAt: '2024-01-13T09:15:00Z',
    paidAt: '2024-01-13T10:30:00Z',
    rapydCheckoutId: 'checkout_125'
  }
];

const PaymentsManagement = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateFilterType, setDateFilterType] = useState('created'); // 'created' or 'paid'
  const [sortBy, setSortBy] = useState('createdAt'); // 'name', 'email', 'amount', 'createdAt', 'paidAt'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isRapydEnabled = process.env.REACT_APP_ENABLE_RAPYD === 'true';

  // Filter and sort payments
  useEffect(() => {
    let filtered = payments;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(payment => 
        payment.clientName.toLowerCase().includes(query) ||
        payment.clientEmail.toLowerCase().includes(query) ||
        payment.description.toLowerCase().includes(query) ||
        payment.clientId.toLowerCase().includes(query) ||
        payment.amount.toString().includes(query)
      );
    }

    // Apply date range filter
    if (dateFrom || dateTo) {
      filtered = filtered.filter(payment => {
        const paymentDate = dateFilterType === 'created' 
          ? new Date(payment.createdAt)
          : payment.paidAt ? new Date(payment.paidAt) : null;

        if (!paymentDate) return false;

        if (dateFrom && dateTo) {
          const fromDate = new Date(dateFrom);
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999); // Include the entire end date
          return paymentDate >= fromDate && paymentDate <= toDate;
        } else if (dateFrom) {
          const fromDate = new Date(dateFrom);
          return paymentDate >= fromDate;
        } else if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          return paymentDate <= toDate;
        }
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.clientName.toLowerCase();
          bValue = b.clientName.toLowerCase();
          break;
        case 'email':
          aValue = a.clientEmail.toLowerCase();
          bValue = b.clientEmail.toLowerCase();
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'paidAt':
          aValue = a.paidAt ? new Date(a.paidAt) : new Date(0);
          bValue = b.paidAt ? new Date(b.paidAt) : new Date(0);
          break;
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPayments(filtered);
  }, [payments, statusFilter, searchQuery, dateFrom, dateTo, dateFilterType, sortBy, sortOrder]);

  const clearDateFilters = () => {
    setDateFrom('');
    setDateTo('');
  };

  // Fetch client details for given user IDs
  const fetchClientDetails = async (clientIds) => {
    if (!isRapydEnabled || clientIds.length === 0) return {};

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_SERVER}users/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds: clientIds })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          return result.data;
        }
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
    return {};
  };

  // Fetch payments from API
  const fetchPayments = async () => {
    if (!user?.sub || !isRapydEnabled) {
      setPayments(mockPayments);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const apiUrl = `${process.env.REACT_APP_SERVER}payment/expert/${user.sub}/requests`;
      console.log('PaymentsManagement: Fetching from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch payments: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('PaymentsManagement: API response:', result);

      if (result.success && result.data) {
        // Get unique client IDs
        const clientIds = [...new Set(result.data.map(payment => payment.clientId))];
        console.log('PaymentsManagement: Fetching client details for IDs:', clientIds);

        // Fetch client details
        const clientDetails = await fetchClientDetails(clientIds);
        console.log('PaymentsManagement: Client details:', clientDetails);

        // Transform backend data to frontend format with client details
        const transformedPayments = result.data.map(payment => {
          const clientInfo = clientDetails[payment.clientId];
          const clientData = clientInfo?.data || {};

          // Build name with fallback
          let clientName = 'N/A';
          if (clientData.given_name || clientData.family_name) {
            clientName = `${clientData.given_name || ''} ${clientData.family_name || ''}`.trim();
          } else if (clientData.name) {
            clientName = clientData.name;
          }

          return {
            id: payment._id,
            clientId: payment.clientId || 'N/A',
            clientName: clientName,
            clientEmail: clientData.email || 'N/A',
            amount: payment.amount || 0,
            currency: payment.currency || 'USD',
            description: payment.description || 'N/A',
            status: payment.status || 'unknown',
            createdAt: payment.createdAt || new Date().toISOString(),
            paidAt: payment.paidAt || null,
            rapydCheckoutId: payment.rapydCheckoutId || 'N/A'
          };
        });
        console.log('PaymentsManagement: Transformed payments:', transformedPayments);
        setPayments(transformedPayments);
      } else {
        throw new Error(result.message || 'Failed to fetch payments');
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      setError(error.message);
      setPayments(mockPayments);
    } finally {
      setIsLoading(false);
    }
  };

  // View payment details
  const viewPaymentDetails = async (paymentId) => {
    if (!isRapydEnabled) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_SERVER}payment/request/${paymentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Payment details:', result.data);
          // You can add a modal to show payment details here
        }
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  // Load payments on component mount
  useEffect(() => {
    if (user?.sub) {
      console.log('PaymentsManagement: Loading payments for user:', user.sub);
      console.log('PaymentsManagement: Rapyd enabled:', isRapydEnabled);
      console.log('PaymentsManagement: Server URL:', process.env.REACT_APP_SERVER);
      fetchPayments();
    }
  }, [user, isRapydEnabled]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock />;
      case 'paid':
        return <FaCheck />;
      case 'expired':
        return <FaExclamationTriangle />;
      case 'cancelled':
        return <FaExclamationTriangle />;
      default:
        return <FaClock />;
    }
  };

  const renderPaymentsContent = () => (
    <>
      <Header>
        <Title>
          <FaCreditCard />
          Payments Management
        </Title>
        <RefreshButton onClick={fetchPayments} disabled={isLoading} spinning={isLoading}>
          <FaSync />
          Refresh
        </RefreshButton>
      </Header>

      <SearchAndFilterBar>
        <SearchBar>
          <FaSearch />
          <SearchInput
            type="text"
            placeholder="Search by name, email, description, user ID, or amount..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <ClearButton onClick={() => setSearchQuery('')} title="Clear search">
              <FaTimes />
            </ClearButton>
          )}
        </SearchBar>

        <FilterBar>
          <FaFilter style={{ color: THEME.primary, fontSize: '16px' }} />
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All ({payments.length})
          </FilterButton>
          <FilterButton
            active={statusFilter === 'pending'}
            onClick={() => setStatusFilter('pending')}
          >
            Pending ({payments.filter(p => p.status === 'pending').length})
          </FilterButton>
          <FilterButton
            active={statusFilter === 'paid'}
            onClick={() => setStatusFilter('paid')}
          >
            Paid ({payments.filter(p => p.status === 'paid').length})
          </FilterButton>
          <FilterButton
            active={statusFilter === 'expired'}
            onClick={() => setStatusFilter('expired')}
          >
            Expired ({payments.filter(p => p.status === 'expired').length})
          </FilterButton>
          <FilterButton
            active={statusFilter === 'cancelled'}
            onClick={() => setStatusFilter('cancelled')}
          >
            Cancelled ({payments.filter(p => p.status === 'cancelled').length})
          </FilterButton>

          <div style={{ width: '1px', height: '24px', background: 'rgba(255, 255, 255, 0.1)', margin: '0 4px' }} />

          <FaCalendarAlt style={{ color: THEME.primary, fontSize: '16px' }} />
          <FilterButton
            active={dateFilterType === 'created'}
            onClick={() => setDateFilterType('created')}
            style={{ padding: '8px 12px' }}
          >
            Created
          </FilterButton>
          <FilterButton
            active={dateFilterType === 'paid'}
            onClick={() => setDateFilterType('paid')}
            style={{ padding: '8px 12px' }}
          >
            Paid
          </FilterButton>

          <DateInput
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From"
            title="From Date"
          />
          <DateInput
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To"
            title="To Date"
          />

          {(dateFrom || dateTo) && (
            <ClearButton onClick={clearDateFilters} title="Clear dates">
              <FaTimes />
            </ClearButton>
          )}

          <div style={{ width: '1px', height: '24px', background: 'rgba(255, 255, 255, 0.1)', margin: '0 4px' }} />

          <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="createdAt">Sort by Created Date</option>
            <option value="paidAt">Sort by Paid Date</option>
            <option value="name">Sort by Name</option>
            <option value="email">Sort by Email</option>
            <option value="amount">Sort by Amount</option>
          </SortSelect>

          <SortOrderButton onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
            {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
          </SortOrderButton>
        </FilterBar>
      </SearchAndFilterBar>

      {error && (
        <ErrorMessage>
          <FaExclamationTriangle />
          {error}
        </ErrorMessage>
      )}

      {isLoading ? (
        <LoadingSpinner>
          <FaSync />
          Loading payments...
        </LoadingSpinner>
      ) : (
        <TableWrapper>
          <TableHeader>
            <div>#</div>
            <div>Name</div>
            <div>Email</div>
            <div>User ID</div>
            <div>Amount</div>
            <div>Status</div>
            <div>Created</div>
            <div>Paid</div>
            <div>Actions</div>
          </TableHeader>

          <TableBody>
            {filteredPayments.length === 0 ? (
              <EmptyState>
                <FaCreditCard />
                <h3>No payments found</h3>
                <p>
                  {statusFilter === 'all'
                    ? 'You haven\'t received any payment requests yet.'
                    : `No ${statusFilter} payments found.`
                  }
                </p>
              </EmptyState>
            ) : (
              filteredPayments.map((payment, index) => (
                <PaymentRow key={payment.id}>
                  <RowNumber>{index + 1}</RowNumber>

                  <NameWithDescription>
                    <ClientName>{payment.clientName}</ClientName>
                    <Description title={payment.description}>
                      {payment.description}
                    </Description>
                  </NameWithDescription>

                  <ClientEmail>{payment.clientEmail}</ClientEmail>

                  <Auth0Id title={payment.clientId}>
                    {payment.clientId}
                  </Auth0Id>

                  <Amount>${payment.amount.toFixed(2)}</Amount>

                  <StatusBadge status={payment.status}>
                    {getStatusIcon(payment.status)}
                    {payment.status}
                  </StatusBadge>

                  <DateText>{formatDate(payment.createdAt)}</DateText>

                  <DateText>
                    {payment.paidAt ? formatDate(payment.paidAt) : '-'}
                  </DateText>

                  <ActionButton onClick={() => viewPaymentDetails(payment.id)}>
                    <FaEye />
                    View
                  </ActionButton>
                </PaymentRow>
              ))
            )}
          </TableBody>
        </TableWrapper>
      )}
    </>
  );

  return (
    <Container>
      {isRapydEnabled ? (
        renderPaymentsContent()
      ) : (
        <BlurredComponent>
          {renderPaymentsContent()}
        </BlurredComponent>
      )}
    </Container>
  );
};

export default PaymentsManagement;